import { useState, useEffect, useRef } from "react";
import { supabase, TechnicalCooperationRow } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import ImageCropper from "../../../components/base/ImageCropper";
import RichTextEditor from "../../../components/base/RichTextEditor";

const EDGE_URL = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/tech-cooperation-api`;

const DEFAULT_CATEGORIES = [
  "Tổng hợp",
  "Công nghệ",
  "Công nghiệp",
  "Kinh tế",
  "Chính sách",
  "Thị trường",
  "Năng lượng",
  "Y tế",
  "Môi trường",
  "Giáo dục",
  "Thương mại",
  "Tài chính",
  "Vật liệu",
  "Bán dẫn",
];

const T = (lang: "vi" | "kr", vi: string, kr: string) => (lang === "vi" ? vi : kr);

interface Props {
  lang: "vi" | "kr";
}

const TechCooperationManager = ({ lang }: Props) => {
  const [items, setItems] = useState<TechnicalCooperationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<TechnicalCooperationRow> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [coverImages, setCoverImages] = useState<string[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [categoryInput, setCategoryInput] = useState("");
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const catDropdownRef = useRef<HTMLDivElement>(null);

  const friendlyError = (msg: string): string => {
    if (msg.includes("new row violates")) {
      return T(
        lang,
        "Không thể lưu do chính sách bảo mật. Vui lòng thử lại sau.",
        "보안 정책으로 저장할 수 없습니다. 나중에 다시 시도해 주세요."
      );
    }
    if (msg.includes("invalid input syntax for type uuid")) {
      return T(
        lang,
        "ID bài viết không hợp lệ. Đừng nhập ID — hệ thống sẽ tự tạo.",
        "게시물 ID가 유효하지 않습니다. ID를 입력하지 마십시오 — 시스템이 자동으로 생성합니다."
      );
    }
    if (msg.includes("timeout") || msg.includes("fetch")) {
      return T(
        lang,
        "Kết nối chậm, vui lòng kiểm tra internet và thử lại.",
        "연결이 느립니다. 인터넷을 확인하고 다시 시도해 주세요."
      );
    }
    return msg;
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${EDGE_URL}?limit=200`, { method: "GET" });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "Fetch failed");
      }
      const rows: TechnicalCooperationRow[] = (json.data ?? []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        title: r.title as string,
        category: (r.category as string) || undefined,
        content: (r.content as string) || undefined,
        image_url: (r.image_url as string) || undefined,
        created_at: (r.created_at as string) || undefined,
        updated_at: (r.updated_at as string) || undefined,
      }));
      setItems(rows);
      const dbCats = rows.map((r) => r.category).filter(Boolean) as string[];
      setDynamicCategories((prev) => [...new Set([...prev, ...dbCats])]);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(
        T(
          lang,
          "Không tải được danh sách. Kiểm tra kết nối và thử lại.",
          "목록을 불러올 수 없습니다. 연결을 확인하고 다시 시도해 주세요."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (catDropdownRef.current && !catDropdownRef.current.contains(e.target as Node)) {
        setShowCatDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openNew = () => {
    setEditing({
      title: "",
      category: "Tổng hợp",
      content: "",
      image_url: "",
    });
    setCoverImages([]);
    setCategoryInput("Tổng hợp");
    setError("");
    setPreviewMode(false);
  };

  const openEdit = (item: TechnicalCooperationRow) => {
    setEditing({ ...item });
    setCoverImages(item.image_url ? [item.image_url] : []);
    setCategoryInput(item.category ?? "");
    setError("");
    setPreviewMode(false);
  };

  const set = (field: keyof TechnicalCooperationRow, val: unknown) => {
    setEditing((prev) => (prev ? { ...prev, [field]: val } : prev));
  };

  const handleSave = async () => {
    if (!editing?.title?.trim()) {
      setError(T(lang, "Vui lòng nhập tiêu đề!", "제목을 입력하세요!"));
      return;
    }
    setSaving(true);
    setError("");
    const finalCategory = categoryInput.trim() || editing.category || "Tổng hợp";
    if (finalCategory && !dynamicCategories.includes(finalCategory)) {
      setDynamicCategories((prev) => [...prev, finalCategory]);
    }

    const payload = {
      title: editing.title,
      category: finalCategory,
      content: editing.content ?? "",
      image_url: coverImages[0] ?? editing.image_url ?? "",
    };

    try {
      let res;
      if (editing.id) {
        res = await fetch(EDGE_URL, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: editing.id, ...payload }),
        });
      } else {
        res = await fetch(EDGE_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json.error || "Save failed");
      }
      setEditing(null);
      await fetchItems();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(friendlyError(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`${EDGE_URL}?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({ error: "Delete failed" }));
        throw new Error(json.error || "Delete failed");
      }
      setDeleteConfirm(null);
      await fetchItems();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(friendlyError(msg));
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-800">
            {T(lang, "Hợp Tác Kỹ Thuật", "기술 협력")}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {T(
              lang,
              "Quản lý bài viết hợp tác kỹ thuật Hàn Quốc – Việt Nam",
              "한국 – 베트남 기술 협력 기사 관리"
            )}
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-xl hover:bg-[#003a7a] cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-line" />
          {T(lang, "Thêm Bài Mới", "새 글 추가")}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
          <i className="ri-file-list-3-line text-4xl text-gray-200 block mb-4" />
          <p className="text-gray-500 text-sm">
            {T(lang, "Chưa có bài viết nào. Hãy thêm bài đầu tiên!", "글을 추가해 보세요!")}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl border border-gray-200 p-5 flex gap-4"
            >
              <div className="w-28 h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.title}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <i className="ri-file-list-3-line text-gray-300 text-2xl" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {item.category && (
                    <span className="text-[10px] bg-[#EDF2FB] text-[#004A99] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                      {item.category}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-gray-900 text-sm line-clamp-1 mb-1">
                  {item.title}
                </h3>
                <div className="flex items-center gap-3 text-[10px] text-gray-400">
                  <span>
                    <i className="ri-calendar-line mr-1" />
                    {formatDate(item.created_at)}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => openEdit(item)}
                  className="p-2 hover:bg-[#EDF2FB] rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-edit-line text-[#004A99]" />
                </button>
                <button
                  onClick={() => setDeleteConfirm(item.id)}
                  className="p-2 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-delete-bin-line text-red-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-bold text-gray-900 text-center mb-2">
              {T(lang, "Xóa bài viết này?", "이 글을 삭제할까요?")}
            </h3>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                {T(lang, "Hủy", "취소")}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-red-600"
              >
                {T(lang, "Xóa", "삭제")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#004A99] flex items-center justify-center">
                  <i className="ri-file-list-3-line text-white text-base" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 text-sm">
                    {T(lang, "Bài viết Hợp tác kỹ thuật", "기술 협력 글")}
                  </h2>
                  <p className="text-[11px] text-gray-400">
                    {T(lang, "Biên tập & lưu bài viết", "글 편집 및 저장")}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className={`px-3 py-1.5 text-xs rounded-lg font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    previewMode
                      ? "bg-[#004A99] text-white"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <i className={`${previewMode ? "ri-edit-line" : "ri-eye-line"} mr-1`} />
                  {previewMode
                    ? T(lang, "Chỉnh sửa", "수정")
                    : T(lang, "Xem trước", "미리보기")}
                </button>
                <button
                  onClick={() => setEditing(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                >
                  <i className="ri-close-line text-gray-500" />
                </button>
              </div>
            </div>

            {previewMode ? (
              <div className="p-6">
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {coverImages[0] && (
                    <img
                      src={coverImages[0]}
                      alt={editing.title ?? ""}
                      className="w-full h-64 object-cover object-top"
                    />
                  )}
                  <div className="p-6">
                    {editing.category && (
                      <span className="text-[10px] bg-[#EDF2FB] text-[#004A99] px-2 py-0.5 rounded-full font-semibold">
                        {editing.category}
                      </span>
                    )}
                    <h1 className="text-2xl font-black text-gray-900 mb-4 mt-3">
                      {editing.title ?? T(lang, "Tiêu đề bài", "제목")}
                    </h1>
                    <div
                      className="article-content"
                      dangerouslySetInnerHTML={{ __html: editing.content ?? "" }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {error && (
                  <div className="bg-red-50 text-red-700 text-xs rounded-lg px-3 py-2 border border-red-200">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="relative" ref={catDropdownRef}>
                    <label className={labelCls}>
                      {T(lang, "Danh mục (gõ mới hoặc chọn)", "카테고리")}
                    </label>
                    <input
                      className={inputCls}
                      placeholder={T(
                        lang,
                        "Gõ hoặc chọn danh mục...",
                        "카테고리 입력 또는 선택..."
                      )}
                      value={categoryInput}
                      onChange={(e) => {
                        setCategoryInput(e.target.value);
                        set("category", e.target.value);
                        setShowCatDropdown(true);
                      }}
                      onFocus={() => setShowCatDropdown(true)}
                    />
                    {showCatDropdown && (
                      <div className="absolute z-50 top-full left-0 right-0 bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto shadow-xl">
                        {dynamicCategories
                          .filter(
                            (c) =>
                              !categoryInput ||
                              c.toLowerCase().includes(categoryInput.toLowerCase())
                          )
                          .map((c) => (
                            <button
                              key={c}
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setCategoryInput(c);
                                set("category", c);
                                setShowCatDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-[#EDF2FB] cursor-pointer whitespace-nowrap"
                            >
                              {c}
                            </button>
                          ))}
                        {categoryInput && !dynamicCategories.includes(categoryInput) && (
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setShowCatDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-[#004A99] font-semibold hover:bg-[#EDF2FB] cursor-pointer whitespace-nowrap"
                          >
                            + Thêm mới: "{categoryInput}"
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>
                    {T(lang, "Tiêu đề bài *", "제목 *")}
                  </label>
                  <input
                    className={inputCls}
                    placeholder={T(lang, "Nhập tiêu đề hấp dẫn...", "매력적인 제목 입력...")}
                    value={editing.title ?? ""}
                    onChange={(e) => set("title", e.target.value)}
                  />
                </div>

                <ImageCropper
                  imageUrl={coverImages[0] ?? ""}
                  onChange={(url) => {
                    setCoverImages(url ? [url] : []);
                    set("image_url", url);
                  }}
                  folder="tech-cooperation"
                  label={T(
                    lang,
                    "Ảnh bìa bài viết (Hỗ trợ cắt 16:9 / 4:3 / 1:1, zoom)",
                    "커버 이미지 (16:9 / 4:3 / 1:1 크롭, 줌)"
                  )}
                  defaultAspect="16:9"
                />

                <RichTextEditor
                  value={editing.content ?? ""}
                  onChange={(html) => set("content", html)}
                  placeholder={T(
                    lang,
                    "Nhập nội dung bài viết đầy đủ tại đây...",
                    "글 내용을 여기에 입력하세요..."
                  )}
                  minHeight={320}
                  folder="tech-cooperation"
                  label={T(lang, "Nội dung bài viết", "글 내용")}
                />
              </div>
            )}

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="text-sm text-[#004A99] font-medium cursor-pointer"
              >
                <i
                  className={`${previewMode ? "ri-edit-line" : "ri-eye-line"} mr-1`}
                />
                {previewMode
                  ? T(lang, "Tiếp tục chỉnh sửa", "계속 수정")
                  : T(lang, "Xem trước bài", "미리보기")}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  {T(lang, "Hủy", "취소")}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <i className="ri-loader-4-line animate-spin" />}
                  <i className="ri-save-line" />
                  {T(lang, "Lưu & Đăng", "저장 및 게시")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TechCooperationManager;